/**
 * Responsible-gambling domain — self-exclusion and cool-off.
 *
 * The backend has had this since migration 0010: a table, a repo, an internal
 * resolver, an enforced gate, and a route the gateway already exposes to the
 * `client` audience where `resolveSelfMemberId` correctly lets a player target
 * only their own id.
 *
 * The player app never called it. `grep -rn "self-exclusion" apps/web/player/src`
 * returned zero hits, so in practice a player who wanted to stop had to ask
 * support to stop them — which inverts the entire point of the control.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api/client.js';

export const RG_QUERY_KEYS = {
  selfExclusions: ['rg', 'self-exclusions'] as const,
} as const;

export type ExclusionKind = 'cooloff' | 'self_exclusion';

export interface SelfExclusionRow {
  id: string;
  memberId: string;
  kind: ExclusionKind;
  reason: string | null;
  expiresAt: string | null;
  liftedAt: string | null;
  setByKind: 'member' | 'admin' | null;
  createdAt: string;
}

export function fetchSelfExclusions(): Promise<{ items: SelfExclusionRow[] }> {
  return apiFetch<{ items: SelfExclusionRow[] }>('/member-self-exclusions');
}

export function useSelfExclusions() {
  return useQuery({
    queryKey: RG_QUERY_KEYS.selfExclusions,
    queryFn: fetchSelfExclusions,
    staleTime: 30_000,
  });
}

/** The active exclusion, if any — a row not lifted and not expired. */
export function activeExclusion(
  rows: SelfExclusionRow[] | undefined,
  now = Date.now(),
): SelfExclusionRow | null {
  if (!rows) return null;
  return (
    rows.find(
      (r) => !r.liftedAt && (r.expiresAt === null || new Date(r.expiresAt).getTime() > now),
    ) ?? null
  );
}

export interface SetExclusionInput {
  kind: ExclusionKind;
  /** null = indefinite. Only a self-exclusion may be indefinite. */
  durationHours: number | null;
  reason?: string;
}

export function setSelfExclusion(input: SetExclusionInput): Promise<SelfExclusionRow> {
  return apiFetch<SelfExclusionRow>('/member-self-exclusions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * The cool-off options a player may pick, in hours.
 *
 * Deliberately coarse and one-way: a player choosing to stop should not have to
 * think about a number, and there is no "shorten it" control anywhere — lifting
 * early is an operator action by design.
 */
export const COOLOFF_OPTIONS = [24, 24 * 7, 24 * 30] as const;
